import type { MouseEvent as ReactMouseEvent } from 'react'
import type { SunoPrompt, SunoPromptPool } from '../../../api/suno'
import type { PlayableTrack } from '../../audio/useAudioPlayer'
import {
  PromptLibraryMobileCard,
  PromptLibraryTableRow,
  type PromptActionMenuPosition
} from './PromptManagementItems'

export type PromptLibraryItemActions = {
  onCopyText: (value: string, label: string) => Promise<void>
  onDeletePrompt: (prompt: SunoPrompt) => void
  onDissolvePromptPool: (poolId: string) => Promise<void>
  onEditPrompt: (prompt: SunoPrompt) => void
  onOpenPromptPoolEditor: (poolId: string) => Promise<void>
  onOpenSimilarity: (prompt: SunoPrompt) => void
  onOpenTracks: (prompt: SunoPrompt) => void
  onPlayPromptTracks: (prompt: SunoPrompt) => Promise<void>
  onRemovePromptPoolVariant: (pool: SunoPromptPool, promptId: string) => Promise<void>
  onSetPromptPoolCanonical: (pool: SunoPromptPool, promptId: string) => Promise<void>
  onTogglePromptPoolExpansion: (poolId: string, anchorPromptId: string) => void
}

type PromptLibraryPlaybackState = {
  audioLoading: boolean
  audioPlaying: boolean
  loadingPromptId: string | null
  playbackPromptId: string | null
  playbackTracks: PlayableTrack[]
  playingTrack: PlayableTrack | null
}

type PromptLibraryRenderersOptions = {
  actionMenuId: string | null
  actionMenuPosition: PromptActionMenuPosition | null
  expandedPromptPoolAnchorPromptIds: Record<string, string | undefined>
  expandedPromptPoolIds: Set<string>
  itemActions: PromptLibraryItemActions
  onCloseActionMenu: () => void
  onToggleActionMenu: (event: ReactMouseEvent<HTMLButtonElement>, menuId: string) => void
  playbackState: PromptLibraryPlaybackState
}

type PromptTableRowOptions = {
  isCurrentPromptVariant?: boolean
  isExpanded?: boolean
  isPoolVariant?: boolean
  pool?: SunoPromptPool | undefined
}

type PromptMobileCardOptions = {
  isPoolVariant?: boolean
  pool?: SunoPromptPool | undefined
}

export const createPromptLibraryRenderers = ({
  actionMenuId,
  actionMenuPosition,
  expandedPromptPoolAnchorPromptIds,
  expandedPromptPoolIds,
  itemActions,
  onCloseActionMenu,
  onToggleActionMenu,
  playbackState
}: PromptLibraryRenderersOptions) => ({
  renderMobileCard: (
    prompt: SunoPrompt,
    options: PromptMobileCardOptions = {}
  ) => (
    <PromptLibraryMobileCard
      {...itemActions}
      {...playbackState}
      expandedPromptPoolAnchorPromptIds={expandedPromptPoolAnchorPromptIds}
      expandedPromptPoolIds={expandedPromptPoolIds}
      isPoolVariant={options.isPoolVariant}
      pool={options.pool}
      prompt={prompt}
    />
  ),
  renderTableRow: (
    prompt: SunoPrompt,
    options: PromptTableRowOptions = {}
  ) => (
    <PromptLibraryTableRow
      {...itemActions}
      {...playbackState}
      actionMenuId={actionMenuId}
      actionMenuPosition={actionMenuPosition}
      isCurrentPromptVariant={options.isCurrentPromptVariant}
      isExpanded={options.isExpanded}
      isPoolVariant={options.isPoolVariant}
      onCloseActionMenu={onCloseActionMenu}
      onToggleActionMenu={onToggleActionMenu}
      pool={options.pool}
      prompt={prompt}
    />
  )
})
